import { useState } from "react";
import { FiFileText, FiX } from "react-icons/fi";
import { FaFileUpload } from "react-icons/fa";
import { storage } from "../../utils/firebase";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";

export default function PdfUploader({ onUploadComplete }) {
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const handleFile = (selectedFile) => {
    if (!selectedFile) return;

    if (selectedFile.type !== "application/pdf") {
      setError("Only PDF files are allowed");
      return;
    }

    if (selectedFile.size > 4 * 1024 * 1024) {
      setError("File size should be less than 4MB");
      return;
    }

    setError("");
    setFile(selectedFile);
    uploadFile(selectedFile);
  };

  const uploadFile = (file) => {
    setUploading(true);

    const storageRef = ref(
      storage,
      `qr-menus/${Date.now()}-${file.name}`
    );

    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        const progressPercent =
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        setProgress(progressPercent);
      },
      () => {
        setError("Upload failed. Try again.");
        setUploading(false);
      },
      async () => {
        const url = await getDownloadURL(uploadTask.snapshot.ref);
        setUploading(false);
        onUploadComplete(url);
      }
    );
  };

  const removeFile = () => {
    setFile(null);
    setProgress(0);
    setUploading(false);
  };

  return (
    <div className="w-full">

      {/* 🎴 DARK CARD */}
      <div className="bg-white/[0.03] border border-white/[0.08] rounded-2xl p-5 space-y-5 backdrop-blur-sm">

        {/* 🔝 HEADER */}
        <div>
          <p className="text-xs text-white/60 uppercase tracking-widest">
            Upload Menu
          </p>
          <p className="text-xs text-white/30 mt-1">
            PDF only · Max 4MB
          </p>
        </div>

        {!file ? (
          <label className="flex flex-col items-center justify-center border border-dashed border-white/[0.15] rounded-xl py-10 cursor-pointer hover:border-white/30 hover:bg-white/[0.03] transition-all duration-200">

            <div className="w-12 h-12 rounded-xl bg-white/[0.04] border border-white/[0.08] flex items-center justify-center mb-3">
              <FaFileUpload className="w-5 h-5 text-white/40" />
            </div>

            <p className="text-sm text-white/60">
              Click to upload your PDF
            </p>

            <p className="text-xs text-white/30 mt-1">
              or drag & drop
            </p>

            <input
              type="file"
              accept="application/pdf"
              className="hidden"
              onChange={(e) => handleFile(e.target.files[0])}
            />
          </label>
        ) : (
          <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4">

            {/* 📄 FILE INFO */}
            <div className="flex items-center justify-between">

              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-white/[0.04] border border-white/[0.08] flex items-center justify-center">
                  <FiFileText className="w-4 h-4 text-white/50" />
                </div>

                <div>
                  <p className="text-sm text-white/70 truncate max-w-[140px]">
                    {file.name}
                  </p>
                  <p className="text-xs text-white/30">
                    {(file.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
              </div>

              <button
                onClick={removeFile}
                className="p-1.5 rounded-md hover:bg-white/[0.08] transition"
              >
                <FiX className="w-4 h-4 text-white/40 hover:text-white" />
              </button>
            </div>

            {/* 📊 PROGRESS */}
            {uploading && (
              <div className="mt-4">

                <div className="flex justify-between text-[11px] text-white/30 mb-1">
                  <span>Uploading</span>
                  <span>{Math.round(progress)}%</span>
                </div>

                <div className="h-1 bg-white/[0.08] rounded-full overflow-hidden">
                  <div
                    className="h-full bg-white/80 transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  />
                </div>

              </div>
            )}

          </div>
        )}

        {/* ❌ ERROR */}
        {error && (
          <p className="text-red-400 text-xs">{error}</p>
        )}

      </div>
    </div>
  );
}